<ng-container *transloco="let t">
  <div class="two-fa">
    <div class="channel-icon" aria-hidden="true">
      @switch (icon) {
        @case ('PUSH') {
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="6" y="2" width="12" height="20" rx="2"></rect>
            <path d="M10 18h4"></path>
            <path d="M12 7v4"></path>
            <circle cx="12" cy="13.5" r="0.6"></circle>
          </svg>
        }
        @case ('EMAIL') {
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="5" width="18" height="14" rx="2"></rect>
            <path d="M3 7l9 6 9-6"></path>
          </svg>
        }
        @case ('SMS') {
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="6" y="2" width="12" height="20" rx="2"></rect>
            <path d="M10 18h4"></path>
          </svg>
        }
        @case ('AUTHENTICATOR_APP') {
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="6" y="2" width="12" height="20" rx="2"></rect>
            <circle cx="12" cy="10" r="3.5"></circle>
            <path d="M12 8.5V10l1 1"></path>
            <path d="M10 18h4"></path>
          </svg>
        }
        @default {
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="4" y="11" width="16" height="10" rx="2"></rect>
            <path d="M8 11V7a4 4 0 0 1 8 0v4"></path>
          </svg>
        }
      }
    </div>

    <h1 class="two-fa-title">{{ t(keyPrefix + 'title') }}</h1>

    <!-- An ended screen only explains why; the instruction no longer applies. -->
    @if (!endedReason) {
      @if (descriptionParts(t(descriptionKey, descriptionParams)); as parts) {
        <p class="two-fa-description">
          {{ parts[0] }}@if (parts[1]) {<strong>{{ parts[1] }}</strong>}{{ parts[2] }}
        </p>
      }

      @if (isPush && authInteraction.challenge_number) {
        <div class="challenge">
          <span class="challenge-number">{{ authInteraction.challenge_number }}</span>
          <span class="challenge-caption">
            {{
              t('lang.auth_interaction_push_challenge', {
                number: authInteraction.challenge_number
              })
            }}
          </span>
        </div>
      }
    }

    @if (!endedReason && inputMode !== 'NONE') {
      @if (inputMode === 'DIGITS') {
        <div
          class="digits"
          role="group"
          [attr.aria-label]="t('lang.auth_interaction_code_label')"
          [class.error]="showsCodeError"
          [class.compact]="digits().length > 6">
          @for (digit of digits(); track $index) {
            <input
              #digitInput
              class="digit-input"
              type="tel"
              inputmode="numeric"
              [attr.autocomplete]="$index === 0 ? 'one-time-code' : 'off'"
              [attr.aria-label]="
                t('lang.auth_interaction_two_fa_digit_label', {
                  index: $index + 1,
                  length: digits().length
                })
              "
              [value]="digit"
              [class.filled]="!!digit"
              [class.error]="showsCodeError"
              [disabled]="inputDisabled"
              (focus)="onDigitFocus($event)"
              (input)="onDigitInput($index, $event)"
              (keydown)="onDigitKeyDown($index, $event)"
              (paste)="onDigitPaste($index, $event)" />
          }
        </div>
      } @else {
        <div class="code-form">
          <div class="mz-form-row vertical">
            <div class="mz-label">
              <label for="auth-interaction-code">{{ t(fieldLabelKey) }}</label>
            </div>

            <div class="mz-input">
              <div class="mz-input-container" [class.error]="showsCodeError">
                <input
                  id="auth-interaction-code"
                  type="text"
                  [formControl]="codeControl"
                  [placeholder]="t(fieldPlaceholderKey)"
                  [attr.inputmode]="fieldInputMode"
                  autocomplete="one-time-code"
                  autocapitalize="off"
                  spellcheck="false"
                  (input)="onFieldInput()"
                  (keydown.enter)="onFieldEnter()" />
              </div>
            </div>
          </div>
        </div>
      }
    }

    <div
      class="status-line"
      role="status"
      aria-live="polite"
      [class.error]="status?.tone === 'error'"
      [class.warning]="status?.tone === 'warning'">
      @if (status; as current) {
        @if (current.spinner) {
          <mzic-spinner />
        }
        @if (current.announceKey) {
          <!-- The countdown ticks visually; assistive tech hears one stable sentence. -->
          <span class="visually-hidden">{{ t(current.announceKey) }}</span>
          <span aria-hidden="true">{{ t(current.key, current.params) }}</span>
        } @else {
          <span>{{ t(current.key, current.params) }}</span>
        }
      }
    </div>

    @if (!endedReason) {
      @if (authInteraction.resend_supported) {
        <div class="resend-line">
          @if (resending) {
            <mzic-spinner />
            <span>{{ t('lang.auth_interaction_resending') }}</span>
          } @else if (resendSecondsLeft > 0) {
            <span>
              {{
                t(resendKeys.countdown, {
                  time: formatClock(resendSecondsLeft)
                })
              }}
            </span>
          } @else {
            <span>{{ t(resendKeys.help) }}</span>
            <button
              class="resend-link"
              type="button"
              [disabled]="verifying"
              (click)="onResend()">
              {{ t(resendKeys.action) }}
            </button>
          }
        </div>
      } @else if (!isPush) {
        <!-- A push without a resend link shows no hint. -->
        <div class="resend-line">
          <span>{{ t(noResendHintKey, { provider: providerName }) }}</span>
        </div>
      }

      @if (isPush && expirySecondsLeft > 0) {
        <p class="expiry-note">
          {{
            t('lang.auth_interaction_push_expires', {
              time: formatClock(expirySecondsLeft)
            })
          }}
        </p>
      }
    }
  </div>
</ng-container>
